import { createAction } from "@reduxjs/toolkit";
import type {
  EAutoMessageCategory,
  IAutoMessage,
  IAutoMessageCategory,
  ICreditCard,
  IIdVerificationInfo,
  IMyPurchase,
  IProviderConfig,
  ISubscriptionInfo,
  IUserInfo,
  IVaultMedia,
} from "./account-models";

interface IPage<T> {
  data: T[];
  next: string;
}

interface IRequestError {
  message?: string;
}

interface IAutoMessageFailure {
  category: EAutoMessageCategory;
  errorResponse?: IRequestError;
}

export namespace IAccountActions {
  export enum Type {
    // SET__USER = 'SET__USER',

    GET__USER__INIT = "GET__USER__INIT",
    GET__USER__SUCCESS = "GET__USER__SUCCESS",
    GET__USER__FAILURE = "GET__USER__FAILURE",

    GET__PROVIDER_CONFIG__INIT = "GET__PROVIDER_CONFIG__INIT",
    GET__PROVIDER_CONFIG__SUCCESS = "GET__PROVIDER_CONFIG__SUCCESS",
    GET__PROVIDER_CONFIG__FAILURE = "GET__PROVIDER_CONFIG__FAILURE",

    GET__USERNAME_AVAILABLE__INIT = "GET__USERNAME_AVAILABLE__INIT",
    GET__USERNAME_AVAILABLE__SUCCESS = "GET__USERNAME_AVAILABLE__SUCCESS",
    GET__USERNAME_AVAILABLE__FAILURE = "GET__USERNAME_AVAILABLE__FAILURE",

    PATCH__UPDATE_USER__INIT = "PATCH__UPDATE_USER__INIT",
    PATCH__UPDATE_USER__SUCCESS = "PATCH__UPDATE_USER__SUCCESS",
    PATCH__UPDATE_USER__FAILURE = "PATCH__UPDATE_USER__FAILURE",

    DELETE__USER_LOGOUT__INIT = "DELETE__USER_LOGOUT__INIT",
    DELETE__USER_LOGOUT__SUCCESS = "DELETE__USER_LOGOUT__SUCCESS",
    DELETE__USER_LOGOUT__ERROR = "DELETE__USER_LOGOUT__ERROR",

    GET__GEO_IP__INIT = "GET__GEO_IP__INIT",
    GET__GEO_IP__SUCCESS = "GET__GEO_IP__SUCCESS",
    GET__GEO_IP__FAILURE = "GET__GEO_IP__FAILURE",

    POST__GET_CREDITS__INIT = "POST__GET_CREDITS__INIT",
    POST__GET_CREDITS__SUCCESS = "POST__GET_CREDITS__SUCCESS",
    POST__GET_CREDITS__FAILURE = "POST__GET_CREDITS__FAILURE",
    RESET__POST_GET_CREDITS = "RESET__POST_GET_CREDITS",

    UPDATE__USER = "UPDATE__USER",
    UPDATE__PROVIDER_CONFIG = "UPDATE__PROVIDER_CONFIG",

    RESET__PATCH_UPDATE_USER = "RESET__PATCH_UPDATE_USER",

    RESET__USER_NOTIFICATIONS = "RESET__USER_NOTIFICATIONS",
    RESET__USER_PROFILE = "RESET__USER_PROFILE",

    GET__SAVED_CREDIT_CARDS__INIT = "GET__SAVED_CREDIT_CARDS__INIT",
    GET__SAVED_CREDIT_CARDS__SUCCESS = "GET__SAVED_CREDIT_CARDS__SUCCESS",
    GET__SAVED_CREDIT_CARDS__FAILURE = "GET__SAVED_CREDIT_CARDS__FAILURE",

    GET__SUBSCRIPTIONS__INIT = "GET__SUBSCRIPTIONS__INIT",
    GET__SUBSCRIPTIONS__SUCCESS = "GET__SUBSCRIPTIONS__SUCCESS",
    GET__SUBSCRIPTIONS__FAILURE = "GET__SUBSCRIPTIONS__FAILURE",

    // DELETE__SUBSCRIPTION__INIT = 'DELETE__SUBSCRIPTION__INIT',
    // DELETE__SUBSCRIPTION__SUCCESS = 'DELETE__SUBSCRIPTION__SUCCESS',
    // DELETE__SUBSCRIPTION__FAILURE = 'DELETE__SUBSCRIPTION__FAILURE',

    DELETE__VIDEO_GREETING__INIT = "DELETE__VIDEO_GREETING__INIT",
    DELETE__VIDEO_GREETING__SUCCESS = "DELETE__VIDEO_GREETING__SUCCESS",
    DELETE__VIDEO_GREETING__FAILURE = "DELETE__VIDEO_GREETING__FAILURE",

    SET__MANAGE_SUBSCRIPTIONS_EDITING = "SET__MANAGE_SUBSCRIPTIONS_EDITING",
    TOGGLE__MANAGE_SUBSCRIPTIONS_SUB_SELECTION = "TOGGLE__MANAGE_SUBSCRIPTIONS_SUB_SELECTION",
    TOGGLE__MANAGE_SUBSCRIPTIONS_ALL_SUBS_SELECTION = "TOGGLE__MANAGE_SUBSCRIPTIONS_ALL_SUBS_SELECTION",

    DELETE__SUBSCRIPTIONS__INIT = "DELETE__SUBSCRIPTIONS__INIT",
    DELETE__SUBSCRIPTIONS__SUCCESS = "DELETE__SUBSCRIPTIONS__SUCCESS",
    DELETE__SUBSCRIPTIONS__FAILURE = "DELETE__SUBSCRIPTIONS__FAILURE",

    RESET__DELETE_SUBSCRIPTIONS = "RESET__DELETE_SUBSCRIPTIONS",

    PATCH__REENABLE_SUBSCRIPTION__INIT = "PATCH__REENABLE_SUBSCRIPTION__INIT",
    PATCH__REENABLE_SUBSCRIPTION__SUCCESS = "PATCH__REENABLE_SUBSCRIPTION__SUCCESS",
    PATCH__REENABLE_SUBSCRIPTION__FAILURE = "PATCH__REENABLE_SUBSCRIPTION__FAILURE",

    RESET__PATCH_REENABLE_SUBSCRIPTION = "RESET__PATCH_REENABLE_SUBSCRIPTION",

    PATCH__USER_RANDOM_AVATAR__INIT = "PATCH__USER_RANDOM_AVATAR__INIT",
    PATCH__USER_RANDOM_AVATAR__SUCCESS = "PATCH__USER_RANDOM_AVATAR__SUCCESS",
    PATCH__USER_RANDOM_AVATAR__FAILURE = "PATCH__USER_RANDOM_AVATAR__FAILURE",

    GET__AUTO_MESSAGES__INIT = "GET__AUTO_MESSAGES__INIT",
    GET__AUTO_MESSAGES__SUCCESS = "GET__AUTO_MESSAGES__SUCCESS",
    GET__AUTO_MESSAGES__FAILURE = "GET__AUTO_MESSAGES__FAILURE",

    POST__AUTO_MESSAGE__INIT = "POST__AUTO_MESSAGE__INIT",
    POST__AUTO_MESSAGE__UPDATE_PROGRESS = "POST__AUTO_MESSAGE__UPDATE_PROGRESS",
    POST__AUTO_MESSAGE__SUCCESS = "POST__AUTO_MESSAGE__SUCCESS",
    POST__AUTO_MESSAGE__FAILURE = "POST__AUTO_MESSAGE__FAILURE",

    DELETE__AUTO_MESSAGE__INIT = "DELETE__AUTO_MESSAGE__INIT",
    DELETE__AUTO_MESSAGE__SUCCESS = "DELETE__AUTO_MESSAGE__SUCCESS",
    DELETE__AUTO_MESSAGE__FAILURE = "DELETE__AUTO_MESSAGE__FAILURE",

    PUT__AUTO_MESSAGES__INIT = "PUT__AUTO_MESSAGES__INIT",
    PUT__AUTO_MESSAGES__SUCCESS = "PUT__AUTO_MESSAGES__SUCCESS",
    PUT__AUTO_MESSAGES__FAILURE = "PUT__AUTO_MESSAGES__FAILURE",

    DELETE__AUTO_MESSAGES__INIT = "DELETE__AUTO_MESSAGES__INIT",
    DELETE__AUTO_MESSAGES__SUCCESS = "DELETE__AUTO_MESSAGES__SUCCESS",
    DELETE__AUTO_MESSAGES__FAILURE = "DELETE__AUTO_MESSAGES__FAILURE",

    RESET__AUTO_MESSAGES = "RESET__AUTO_MESSAGES",

    POST__USER_TAG__INIT = "POST__USER_TAG__INIT",
    POST__USER_TAG__SUCCESS = "POST__USER_TAG__SUCCESS",
    POST__USER_TAG__FAILURE = "POST__USER_TAG__FAILURE",

    GET__MY_PURCHASES__INIT = "GET__MY_PURCHASES__INIT",
    GET__MY_PURCHASES__SUCCESS = "GET__MY_PURCHASES__SUCCESS",
    GET__MY_PURCHASES__FAILURE = "GET__MY_PURCHASES__FAILURE",

    GET__MY_PURCHASES_NEXT__INIT = "GET__MY_PURCHASES_NEXT__INIT",
    GET__MY_PURCHASES_NEXT__SUCCESS = "GET__MY_PURCHASES_NEXT__SUCCESS",
    GET__MY_PURCHASES_NEXT__FAILURE = "GET__MY_PURCHASES_NEXT__FAILURE",

    PATCH__HIDE_MY_PURCHASE__INIT = "PATCH__HIDE_MY_PURCHASE__INIT",
    PATCH__HIDE_MY_PURCHASE__SUCCESS = "PATCH__HIDE_MY_PURCHASE__SUCCESS",
    PATCH__HIDE_MY_PURCHASE__FAILURE = "PATCH__HIDE_MY_PURCHASE__FAILURE",

    PATCH__UNHIDE_MY_PURCHASE__INIT = "PATCH__UNHIDE_MY_PURCHASE__INIT",
    PATCH__UNHIDE_MY_PURCHASE__SUCCESS = "PATCH__UNHIDE_MY_PURCHASE__SUCCESS",
    PATCH__UNHIDE_MY_PURCHASE__FAILURE = "PATCH__UNHIDE_MY_PURCHASE__FAILURE",

    REMOVE_EXPIRED_FROM_MY_PURCHASES = "REMOVE_EXPIRED_FROM_MY_PURCHASES",

    GET__ID_VERIFICATION__INIT = "GET__ID_VERIFICATION__INIT",
    GET__ID_VERIFICATION__SUCCESS = "GET__ID_VERIFICATION__SUCCESS",
    GET__ID_VERIFICATION__FAILURE = "GET__ID_VERIFICATION__FAILURE",

    GET__VAULT_MEDIA__INIT = "GET__VAULT_MEDIA__INIT",
    GET__VAULT_MEDIA__SUCCESS = "GET__VAULT_MEDIA__SUCCESS",
    GET__VAULT_MEDIA__FAILURE = "GET__VAULT_MEDIA__FAILURE",

    GET__VAULT_MEDIA_NEXT__INIT = "GET__VAULT_MEDIA_NEXT__INIT",
    GET__VAULT_MEDIA_NEXT__SUCCESS = "GET__VAULT_MEDIA_NEXT__SUCCESS",
    GET__VAULT_MEDIA_NEXT__FAILURE = "GET__VAULT_MEDIA_NEXT__FAILURE",

    DELETE__VAULT_MEDIA__INIT = "DELETE_VAULT_MEDIA__INIT",
    DELETE__VAULT_MEDIA__SUCCESS = "DELETE_VAULT_MEDIA__SUCCESS",
    DELETE__VAULT_MEDIA__FAILURE = "DELETE_VAULT_MEDIA__FAILURE",
  }

  // export const setUser = createAction<unknown>(Type.SET__USER);

  export const initGetUser = createAction<{ isForceLoader?: boolean }>(
    Type.GET__USER__INIT
  );
  export const successGetUser = createAction<IUserInfo>(
    Type.GET__USER__SUCCESS
  );
  export const failureGetUser = createAction<unknown>(Type.GET__USER__FAILURE);

  export const initGetProviderConfig = createAction(
    Type.GET__PROVIDER_CONFIG__INIT
  );
  export const successGetProviderConfig = createAction<IProviderConfig>(
    Type.GET__PROVIDER_CONFIG__SUCCESS
  );
  export const failureGetProviderConfig = createAction<unknown>(
    Type.GET__PROVIDER_CONFIG__FAILURE
  );

  export const initGetUsernameAvailable = createAction<unknown>(
    Type.GET__USERNAME_AVAILABLE__INIT
  );
  export const successGetUsernameAvailable = createAction<unknown>(
    Type.GET__USERNAME_AVAILABLE__SUCCESS
  );
  export const failureGetUsernameAvailable = createAction<unknown>(
    Type.GET__USERNAME_AVAILABLE__FAILURE
  );

  export const initPatchUpdateUser = createAction<unknown>(
    Type.PATCH__UPDATE_USER__INIT
  );
  export const successPatchUpdateUser = createAction<{
    res: IUserInfo;
    newValues?: Partial<IUserInfo>;
  }>(Type.PATCH__UPDATE_USER__SUCCESS);
  export const failurePatchUpdateUser = createAction<unknown>(
    Type.PATCH__UPDATE_USER__FAILURE
  );

  export const resetPatchUpdateUser = createAction(
    Type.RESET__PATCH_UPDATE_USER
  );

  export const initDeleteUserLogout = createAction(
    Type.DELETE__USER_LOGOUT__INIT
  );
  export const successDeleteUserLogout = createAction<unknown>(
    Type.DELETE__USER_LOGOUT__SUCCESS
  );
  export const errorDeleteUserLogout = createAction<unknown>(
    Type.DELETE__USER_LOGOUT__ERROR
  );

  export const initGetGeoIp = createAction(Type.GET__GEO_IP__INIT);
  export const successGetGeoIp = createAction<unknown>(
    Type.GET__GEO_IP__SUCCESS
  );
  export const failureGetGeoIp = createAction<unknown>(
    Type.GET__GEO_IP__FAILURE
  );

  export const initPostGetCredits = createAction<unknown>(
    Type.POST__GET_CREDITS__INIT
  );
  export const successPostGetCredits = createAction<unknown>(
    Type.POST__GET_CREDITS__SUCCESS
  );
  export const failurePostGetCredits = createAction<unknown>(
    Type.POST__GET_CREDITS__FAILURE
  );
  export const resetPostGetCredits = createAction(Type.RESET__POST_GET_CREDITS);

  export const updateUser = createAction<{ newValues: Partial<IUserInfo> }>(
    Type.UPDATE__USER
  );
  export const updateProviderConfig = createAction<{
    newValues: Partial<IProviderConfig>;
  }>(Type.UPDATE__PROVIDER_CONFIG);

  export const resetUpdateUserNotifications = createAction(
    Type.RESET__USER_NOTIFICATIONS
  );
  export const resetUpdateUserProfile = createAction(Type.RESET__USER_PROFILE);

  export const initGetSavedCreditCards = createAction(
    Type.GET__SAVED_CREDIT_CARDS__INIT
  );
  export const successGetSavedCreditCards = createAction<{
    data: ICreditCard[];
  }>(Type.GET__SAVED_CREDIT_CARDS__SUCCESS);
  export const failureGetSavedCreditCards = createAction<unknown>(
    Type.GET__SAVED_CREDIT_CARDS__FAILURE
  );

  export const initGetSubscriptions = createAction<unknown>(
    Type.GET__SUBSCRIPTIONS__INIT
  );
  export const successGetSubscriptions = createAction<{
    config: { isInitialRequest: boolean };
    res: IPage<ISubscriptionInfo> & { total: number };
  }>(Type.GET__SUBSCRIPTIONS__SUCCESS);
  export const failureGetSubscriptions = createAction<unknown>(
    Type.GET__SUBSCRIPTIONS__FAILURE
  );

  export const initDeleteVideoGreeting = createAction(
    Type.DELETE__VIDEO_GREETING__INIT
  );
  export const successDeleteVideoGreeting = createAction<unknown>(
    Type.DELETE__VIDEO_GREETING__SUCCESS
  );
  export const failureDeleteVideoGreeting = createAction<unknown>(
    Type.DELETE__VIDEO_GREETING__FAILURE
  );

  export const setManageSubscriptionsEditing = createAction<boolean>(
    Type.SET__MANAGE_SUBSCRIPTIONS_EDITING
  );
  export const toggleManageSubscriptionsSubSelection =
    createAction<ISubscriptionInfo>(
      Type.TOGGLE__MANAGE_SUBSCRIPTIONS_SUB_SELECTION
    );
  export const toggleManageSubscriptionsAllSubsSelection = createAction(
    Type.TOGGLE__MANAGE_SUBSCRIPTIONS_ALL_SUBS_SELECTION
  );

  export const initDeleteSubscriptions = createAction(
    Type.DELETE__SUBSCRIPTIONS__INIT
  );
  export const successDeleteSubscriptions = createAction<unknown>(
    Type.DELETE__SUBSCRIPTIONS__SUCCESS
  );
  export const failureDeleteSubscriptions = createAction<unknown>(
    Type.DELETE__SUBSCRIPTIONS__FAILURE
  );

  export const resetDeleteSubscriptions = createAction(
    Type.RESET__DELETE_SUBSCRIPTIONS
  );

  export const initPatchReenableSubscription = createAction(
    Type.PATCH__REENABLE_SUBSCRIPTION__INIT
  );
  export const successPatchReenableSubscription = createAction<{
    data: ISubscriptionInfo[];
  }>(Type.PATCH__REENABLE_SUBSCRIPTION__SUCCESS);
  export const failurePatchReenableSubscription = createAction<unknown>(
    Type.PATCH__REENABLE_SUBSCRIPTION__FAILURE
  );

  export const resetPatchReenableSubscription = createAction(
    Type.RESET__PATCH_REENABLE_SUBSCRIPTION
  );

  export const initPatchUserRandomAvatar = createAction(
    Type.PATCH__USER_RANDOM_AVATAR__INIT
  );
  export const successPatchUserRandomAvatar = createAction<IUserInfo | null>(
    Type.PATCH__USER_RANDOM_AVATAR__SUCCESS
  );
  export const failurePatchUserRandomAvatar = createAction<unknown>(
    Type.PATCH__USER_RANDOM_AVATAR__FAILURE
  );

  export const initGetAutoMessages = createAction(
    Type.GET__AUTO_MESSAGES__INIT
  );
  export const successGetAutoMessages = createAction<{
    data: IAutoMessageCategory[];
  }>(Type.GET__AUTO_MESSAGES__SUCCESS);
  export const failureGetAutoMessages = createAction<unknown>(
    Type.GET__AUTO_MESSAGES__FAILURE
  );

  export const initPostAutoMessage = createAction<{
    category: EAutoMessageCategory;
    clientSideAutoMessage: IAutoMessage;
  }>(Type.POST__AUTO_MESSAGE__INIT);
  export const updatePostAutoMessageProgress = createAction<{
    category: EAutoMessageCategory;
    progress: number;
    loaded: number;
    total: number;
    __clientSideSendId: string;
  }>(Type.POST__AUTO_MESSAGE__UPDATE_PROGRESS);
  export const successPostAutoMessage = createAction<{
    category: EAutoMessageCategory;
    response: { data: IAutoMessage };
    __clientSideSendId: string;
  }>(Type.POST__AUTO_MESSAGE__SUCCESS);
  export const failurePostAutoMessage = createAction<
    IAutoMessageFailure & {
      isAbort?: boolean;
      __clientSideSendId: string;
    }
  >(Type.POST__AUTO_MESSAGE__FAILURE);

  export const initDeleteAutoMessage = createAction<{
    category: EAutoMessageCategory;
    autoMessageId: string;
  }>(Type.DELETE__AUTO_MESSAGE__INIT);
  export const successDeleteAutoMessage = createAction<{
    category: EAutoMessageCategory;
    autoMessageId: string;
    response?: unknown;
  }>(Type.DELETE__AUTO_MESSAGE__SUCCESS);
  export const failureDeleteAutoMessage = createAction<
    IAutoMessageFailure & { autoMessageId: string }
  >(Type.DELETE__AUTO_MESSAGE__FAILURE);

  export const initPutAutoMessages = createAction<{
    category: EAutoMessageCategory;
    autoMessages: IAutoMessage[];
  }>(Type.PUT__AUTO_MESSAGES__INIT);
  export const successPutAutoMessages = createAction<{
    category: EAutoMessageCategory;
    autoMessages: IAutoMessage[];
    response: unknown;
  }>(Type.PUT__AUTO_MESSAGES__SUCCESS);
  export const failurePutAutoMessages = createAction<
    IAutoMessageFailure & { autoMessages: IAutoMessage[] }
  >(Type.PUT__AUTO_MESSAGES__FAILURE);

  export const initDeleteAutoMessages = createAction<{
    category: EAutoMessageCategory;
  }>(Type.DELETE__AUTO_MESSAGES__INIT);
  export const successDeleteAutoMessages = createAction<{
    category: EAutoMessageCategory;
    response: unknown;
  }>(Type.DELETE__AUTO_MESSAGES__SUCCESS);
  export const failureDeleteAutoMessages = createAction<IAutoMessageFailure>(
    Type.DELETE__AUTO_MESSAGES__FAILURE
  );

  export const resetAutoMessages = createAction(Type.RESET__AUTO_MESSAGES);

  export const initPostUserTag = createAction<unknown>(
    Type.POST__USER_TAG__INIT
  );
  export const successPostUserTag = createAction<{
    tag: string;
    response: unknown;
  }>(Type.POST__USER_TAG__SUCCESS);
  export const failurePostUserTag = createAction<unknown>(
    Type.POST__USER_TAG__FAILURE
  );

  export const initGetMyPurchases = createAction(Type.GET__MY_PURCHASES__INIT);
  export const successGetMyPurchases = createAction<IPage<IMyPurchase>>(
    Type.GET__MY_PURCHASES__SUCCESS
  );
  export const failureGetMyPurchases = createAction<unknown>(
    Type.GET__MY_PURCHASES__FAILURE
  );

  export const initGetMyPurchasesNext = createAction(
    Type.GET__MY_PURCHASES_NEXT__INIT
  );
  export const successGetMyPurchasesNext = createAction<IPage<IMyPurchase>>(
    Type.GET__MY_PURCHASES_NEXT__SUCCESS
  );
  export const failureGetMyPurchasesNext = createAction<unknown>(
    Type.GET__MY_PURCHASES_NEXT__FAILURE
  );

  export const initPatchHideMyPurchase = createAction(
    Type.PATCH__HIDE_MY_PURCHASE__INIT
  );
  export const successPatchHideMyPurchase = createAction<{
    messageIds: string[];
    response: unknown;
  }>(Type.PATCH__HIDE_MY_PURCHASE__SUCCESS);
  export const failurePatchHideMyPurchase = createAction<unknown>(
    Type.PATCH__HIDE_MY_PURCHASE__FAILURE
  );

  export const initPatchUnhideMyPurchase = createAction(
    Type.PATCH__UNHIDE_MY_PURCHASE__INIT
  );
  export const successPatchUnhideMyPurchase = createAction<{
    messageIds: string[];
    response: unknown;
  }>(Type.PATCH__UNHIDE_MY_PURCHASE__SUCCESS);
  export const failurePatchUnhideMyPurchase = createAction<unknown>(
    Type.PATCH__UNHIDE_MY_PURCHASE__FAILURE
  );

  export const removeExpiredFromMyPurchases = createAction<{
    messageId: string;
  }>(Type.REMOVE_EXPIRED_FROM_MY_PURCHASES);

  export const initGetIdVerification = createAction(
    Type.GET__ID_VERIFICATION__INIT
  );
  export const successGetIdVerification =
    createAction<IIdVerificationInfo | null>(
      Type.GET__ID_VERIFICATION__SUCCESS
    );
  export const failureGetIdVerification = createAction<unknown>(
    Type.GET__ID_VERIFICATION__FAILURE
  );

  export const initGetVaultMedia = createAction(Type.GET__VAULT_MEDIA__INIT);
  export const successGetVaultMedia = createAction<IPage<IVaultMedia>>(
    Type.GET__VAULT_MEDIA__SUCCESS
  );
  export const failureGetVaultMedia = createAction<unknown>(
    Type.GET__VAULT_MEDIA__FAILURE
  );

  export const initGetVaultMediaNext = createAction(
    Type.GET__VAULT_MEDIA_NEXT__INIT
  );
  export const successGetVaultMediaNext = createAction<IPage<IVaultMedia>>(
    Type.GET__VAULT_MEDIA_NEXT__SUCCESS
  );
  export const failureGetVaultMediaNext = createAction<unknown>(
    Type.GET__VAULT_MEDIA_NEXT__FAILURE
  );

  export const initDeleteVaultMedia = createAction(
    Type.DELETE__VAULT_MEDIA__INIT
  );
  export const successDeleteVaultMedia = createAction<{
    mediaIds: string[];
    response: unknown;
  }>(Type.DELETE__VAULT_MEDIA__SUCCESS);
  export const failureDeleteVaultMedia = createAction<unknown>(
    Type.DELETE__VAULT_MEDIA__FAILURE
  );
}

export type AccountActions = Omit<typeof IAccountActions, "Type">;
